import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { 
  BookOpen, 
  LogIn, 
  Shield, 
  FileText, 
  Upload, 
  QrCode, 
  Download,
  Search,
  Edit,
  Trash2,
  Settings,
  Lock,
  Image,
  ChevronDown,
  ChevronUp,
  Globe
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Translations
const translations = {
  en: {
    // Header
    pageTitle: "How to Use TankGuard",
    adminGuide: "Administrator Guide",
    userGuide: "User Guide",
    welcomeText: "Welcome to TankGuard - your secure document and QR management system for tank permissions. This guide will help you understand all the features available to you.",
    
    // Quick Start
    quickStart: "Quick Start Guide",
    step1Title: "Create Permission",
    step1Desc: "Add a new permission with tank IDs",
    step2Title: "Upload Document",
    step2Desc: "Upload PDF with issue & expiry dates",
    step3Title: "Share QR Code",
    step3Desc: "Download document with embedded QR",
    
    // Feature Guide
    featureGuide: "Feature Guide",
    
    // Logging In
    loggingIn: "Logging In",
    loginScreen: "Login Screen",
    loginStep1Title: "Enter Your Username",
    loginStep1Desc: "Type your assigned username in the first field.",
    loginStep2Title: "Enter Your Password",
    loginStep2Desc: "Type your password in the second field.",
    loginStep3Title: "Click Login",
    loginStep3Desc: "Press the Login button to access the system.",
    loginTip: "If you forgot your password, contact your administrator.",
    
    // Viewing Permissions
    viewingPermissions: "Viewing Permissions",
    permissionCardExample: "Permission Card Example",
    permissionIntro: "The Permissions page shows all tank permissions in the system. Each permission card displays:",
    permissionNumber: "Permission Number:",
    permissionNumberDesc: "The unique identifier for this permission",
    statusBadge: "Status Badge:",
    statusBadgeDesc: "Shows if the document is active, pending, or expired",
    tankCount: "Tank Count:",
    tankCountDesc: "Number of tanks assigned to this permission",
    creationDate: "Creation Date:",
    creationDateDesc: "When the permission was created",
    tanksAssigned: "tanks assigned",
    created: "Created:",
    
    // Creating Permission
    creatingPermission: "Creating a New Permission",
    createPermissionDialog: "Create Permission Dialog",
    createPermissionNumber: "Permission Number",
    tankIdsLabel: "Tank IDs (comma separated)",
    createStep1Title: "Click 'Create Permission' Button",
    createStep1Desc: "On the Permissions page, click the blue 'Create Permission' button in the top right.",
    createStep2Title: "Enter Permission Number",
    createStep2Desc: "Type a unique permission number (e.g., 1234, 5678).",
    createStep2Tip: "The permission number must be unique and not already exist in the system.",
    createStep3Title: "Enter Tank IDs",
    createStep3Desc: "Type the tank IDs separated by commas. Example: T-001, T-002, TANK-A1, TANK-B2",
    createStep3Tip: "You can enter up to 400 tank IDs per permission. The same tank can be on multiple permissions.",
    createStep4Title: "Click 'Create Permission'",
    createStep4Desc: "Submit the form to create the new permission.",
    
    // Uploading Documents
    uploadingDocuments: "Uploading Documents",
    uploadDocumentSection: "Upload Document Section",
    uploadNewDocument: "Upload New Document",
    clickToUpload: "Click to upload PDF",
    issueDate: "Issue Date",
    expiryDate: "Expiry Date",
    uploadStep1Title: "Go to Permission Details",
    uploadStep1Desc: "Click on a permission card to open its detail page.",
    uploadStep2Title: "Select PDF File",
    uploadStep2Desc: "Click the upload area and select a PDF document from your computer.",
    uploadStep2Tip: "Only PDF files are accepted. The system will automatically embed a QR code.",
    uploadStep3Title: "Set Issue Date",
    uploadStep3Desc: "Select the date when the document was issued.",
    uploadStep4Title: "Set Expiry Date",
    uploadStep4Desc: "Select when the document expires.",
    uploadStep4Tip: "The expiry date must be after the issue date.",
    uploadStep5Title: "Click 'Upload Document'",
    uploadStep5Desc: "The system will process the PDF, embed a QR code with the Sharjah Customs logo, and save it.",
    
    // QR Codes
    understandingQR: "Understanding QR Codes",
    qrOnDocument: "QR Code on Document",
    qrWithLogo: "QR Code with Sharjah Customs Logo",
    qrLocation: "Located at bottom center of PDF",
    howQRWorks: "How QR Codes Work",
    qrPoint1: "Each permission has a unique QR code",
    qrPoint2: "QR code is automatically embedded when you upload a document",
    qrPoint3: "Scanning the QR code opens the official document",
    qrPoint4: "QR code includes the Sharjah Customs logo for authenticity",
    qrPoint5: "QR codes work 24/7 - no downtime",
    scanningInstructions: "Scanning Instructions",
    scanStep1: "Open your phone's camera app",
    scanStep2: "Point at the QR code on the document",
    scanStep3: "Tap the notification that appears",
    scanStep4: "The document will open automatically",
    
    // Downloading
    downloadingDocuments: "Downloading Documents",
    downloadStep1Title: "Go to Permission Details",
    downloadStep1Desc: "Click on any permission card to view its details.",
    downloadStep2Title: "Click Download Button",
    downloadStep2Desc: "Click the 'Download Document' button to save the PDF with the embedded QR code.",
    downloadStep2Tip: "The downloaded PDF includes the QR code that links directly to this document.",
    downloadStep3Title: "Download QR Code Only",
    downloadStep3Desc: "You can also download just the QR code image by clicking 'Download QR Code'.",
    
    // Password
    changingPassword: "Changing Your Password",
    passwordStep1Title: "Go to Settings",
    passwordStep1Desc: "Click 'Settings' in the navigation menu on the left.",
    passwordStep2Title: "Enter Current Password",
    passwordStep2Desc: "Type your current password to verify your identity.",
    passwordStep3Title: "Enter New Password",
    passwordStep3Desc: "Type your new password (minimum 6 characters).",
    passwordStep3Tip: "Choose a strong password with letters, numbers, and symbols.",
    passwordStep4Title: "Confirm New Password",
    passwordStep4Desc: "Type the new password again to confirm.",
    passwordStep5Title: "Click 'Change Password'",
    passwordStep5Desc: "Your password will be updated immediately.",
    
    // Admin Features
    adminFeatures: "Administrator Features",
    adminFeaturesDesc: "The following features are only available to administrators.",
    
    // Managing Users
    managingUsers: "Managing Users",
    usersManagementPage: "Users Management Page",
    users: "Users",
    administrator: "Administrator",
    regularUser: "Regular User",
    creatingNewUser: "Creating a New User",
    userStep1Title: "Go to Users Page",
    userStep1Desc: "Click 'Users' in the navigation menu.",
    userStep2Title: "Click 'Create User'",
    userStep2Desc: "Click the blue button in the top right corner.",
    userStep3Title: "Fill in User Details",
    userStep3Desc: "Enter username, password, name, employee ID, and select role (admin or user).",
    userStep3Tip: "Regular users can only create permissions. Admins have full access.",
    editingUser: "Editing a User",
    editStep1Title: "Find the User",
    editStep1Desc: "Use the search box to find the user you want to edit.",
    editStep2Title: "Click Edit Button",
    editStep2Desc: "Click the blue 'Edit' button on the user's row.",
    editStep3Title: "Update Information",
    editStep3Desc: "Change name, employee ID, role, or password as needed.",
    deletingUser: "Deleting a User",
    deleteUserStep1Title: "Click Delete Button",
    deleteUserStep1Desc: "Click the red 'Delete' button on the user's row.",
    deleteUserStep1Tip: "You cannot delete your own account or the main admin account.",
    deleteUserStep2Title: "Confirm Deletion",
    deleteUserStep2Desc: "Click 'OK' on the confirmation dialog to permanently delete the user.",
    
    // Deleting Permissions
    deletingPermissions: "Deleting Permissions",
    deleteWarningTitle: "Important Warning",
    deleteWarningText: "Deleting a permission will also delete all associated documents. This action cannot be undone.",
    deletePermStep1Title: "Find the Permission",
    deletePermStep1Desc: "Go to Permissions page and locate the permission you want to delete.",
    deletePermStep2Title: "Click Delete Button",
    deletePermStep2Desc: "Click the red 'Delete' button on the permission card.",
    deletePermStep3Title: "Confirm Deletion",
    deletePermStep3Desc: "Click 'OK' on the confirmation dialog.",
    
    // Audit Logs
    viewingAuditLogs: "Viewing Audit Logs",
    auditLogsPage: "Audit Logs Page",
    auditLogs: "Audit Logs",
    auditLogsIntro: "Audit logs track all important actions in the system:",
    loginAction: "User login attempts (success/failure)",
    userCreatedAction: "New user accounts created",
    userUpdatedAction: "User information changed",
    userDeletedAction: "User accounts removed",
    permissionCreatedAction: "New permissions added",
    permissionUpdatedAction: "Tank IDs modified",
    permissionDeletedAction: "Permissions removed",
    documentUploadedAction: "PDFs uploaded",
    passwordChangedAction: "Password updates",
    auditStep1Title: "Go to Audit Logs",
    auditStep1Desc: "Click 'Audit Logs' in the navigation menu.",
    auditStep2Title: "Filter by Status",
    auditStep2Desc: "Use the status dropdown to filter by success or failure.",
    auditStep3Title: "Filter by Action",
    auditStep3Desc: "Use the action dropdown to filter by specific action types.",
    
    // Login Background
    customizingBackground: "Customizing Login Background",
    bgStep1Title: "Go to Settings",
    bgStep1Desc: "Click 'Settings' in the navigation menu.",
    bgStep2Title: "Scroll to Login Background",
    bgStep2Desc: "Find the 'Login Background Image' section.",
    bgStep3Title: "Click Choose Image",
    bgStep3Desc: "Click the button and select a JPEG, PNG, or WebP image.",
    bgStep3Tip: "For best results, use a high-resolution image (1920x1080 or larger).",
    bgStep4Title: "Wait for Upload",
    bgStep4Desc: "The image will be uploaded and immediately applied to the login page.",
    
    // FAQ
    faq: "Frequently Asked Questions",
    faq1Q: "Can the same tank be on multiple permissions?",
    faq1A: "Yes, the same tank ID can be assigned to multiple permissions.",
    faq2Q: "What happens to the QR code when I upload a new document?",
    faq2A: "A new QR code is generated and embedded in the new document. The old document becomes inactive.",
    faq3Q: "Why does scanning the QR code show a loading page first?",
    faq3A: "The loading page ensures the document loads properly even if the server needs a moment to respond.",
    faq4Q: "Can regular users delete permissions?",
    faq4A: "No, only administrators can delete permissions. Regular users can create and edit permissions.",
    faq5Q: "How do I contact support?",
    faq5A: "Please contact your system administrator for support.",
    
    // Footer
    footer: "TankGuard Document Management System • User Manual v1.0",
    
    // Common
    tip: "Tip:",
    active: "active",
    edit: "Edit",
    delete: "Delete",
    admin: "admin",
    user: "user",
    login: "LOGIN",
    userLoggedIn: "User logged in",
    permissionCreated: "Created #534",
    documentUploaded: "Uploaded PDF",
  },
  ar: {
    // Header
    pageTitle: "كيفية استخدام TankGuard",
    adminGuide: "دليل المسؤول",
    userGuide: "دليل المستخدم",
    welcomeText: "مرحباً بك في TankGuard - نظام إدارة المستندات ورموز QR الآمن لتصاريح الخزانات. سيساعدك هذا الدليل على فهم جميع الميزات المتاحة لك.",
    
    // Quick Start
    quickStart: "دليل البدء السريع",
    step1Title: "إنشاء تصريح",
    step1Desc: "أضف تصريحاً جديداً مع معرفات الخزانات",
    step2Title: "رفع المستند",
    step2Desc: "ارفع ملف PDF مع تواريخ الإصدار والانتهاء",
    step3Title: "مشاركة رمز QR",
    step3Desc: "حمّل المستند مع رمز QR المضمن",
    
    // Feature Guide
    featureGuide: "دليل الميزات",
    
    // Logging In
    loggingIn: "تسجيل الدخول",
    loginScreen: "شاشة تسجيل الدخول",
    loginStep1Title: "أدخل اسم المستخدم",
    loginStep1Desc: "اكتب اسم المستخدم المخصص لك في الحقل الأول.",
    loginStep2Title: "أدخل كلمة المرور",
    loginStep2Desc: "اكتب كلمة المرور في الحقل الثاني.",
    loginStep3Title: "انقر على تسجيل الدخول",
    loginStep3Desc: "اضغط على زر تسجيل الدخول للوصول إلى النظام.",
    loginTip: "إذا نسيت كلمة المرور، تواصل مع المسؤول.",
    
    // Viewing Permissions
    viewingPermissions: "عرض التصاريح",
    permissionCardExample: "مثال على بطاقة التصريح",
    permissionIntro: "تعرض صفحة التصاريح جميع تصاريح الخزانات في النظام. تعرض كل بطاقة تصريح:",
    permissionNumber: "رقم التصريح:",
    permissionNumberDesc: "المعرف الفريد لهذا التصريح",
    statusBadge: "شارة الحالة:",
    statusBadgeDesc: "تُظهر ما إذا كان المستند نشطاً أو معلقاً أو منتهي الصلاحية",
    tankCount: "عدد الخزانات:",
    tankCountDesc: "عدد الخزانات المخصصة لهذا التصريح",
    creationDate: "تاريخ الإنشاء:",
    creationDateDesc: "متى تم إنشاء التصريح",
    tanksAssigned: "خزان مخصص",
    created: "تم الإنشاء:",
    
    // Creating Permission
    creatingPermission: "إنشاء تصريح جديد",
    createPermissionDialog: "نافذة إنشاء التصريح",
    createPermissionNumber: "رقم التصريح",
    tankIdsLabel: "معرفات الخزانات (مفصولة بفواصل)",
    createStep1Title: "انقر على زر 'إنشاء تصريح'",
    createStep1Desc: "في صفحة التصاريح، انقر على الزر الأزرق 'إنشاء تصريح' في أعلى اليمين.",
    createStep2Title: "أدخل رقم التصريح",
    createStep2Desc: "اكتب رقم تصريح فريد (مثال: 1234، 5678).",
    createStep2Tip: "يجب أن يكون رقم التصريح فريداً وغير موجود مسبقاً في النظام.",
    createStep3Title: "أدخل معرفات الخزانات",
    createStep3Desc: "اكتب معرفات الخزانات مفصولة بفواصل. مثال: T-001, T-002, TANK-A1, TANK-B2",
    createStep3Tip: "يمكنك إدخال حتى 400 معرف خزان لكل تصريح. يمكن أن يكون نفس الخزان في تصاريح متعددة.",
    createStep4Title: "انقر على 'إنشاء تصريح'",
    createStep4Desc: "أرسل النموذج لإنشاء التصريح الجديد.",
    
    // Uploading Documents
    uploadingDocuments: "رفع المستندات",
    uploadDocumentSection: "قسم رفع المستند",
    uploadNewDocument: "رفع مستند جديد",
    clickToUpload: "انقر لرفع ملف PDF",
    issueDate: "تاريخ الإصدار",
    expiryDate: "تاريخ الانتهاء",
    uploadStep1Title: "اذهب إلى تفاصيل التصريح",
    uploadStep1Desc: "انقر على بطاقة التصريح لفتح صفحة التفاصيل.",
    uploadStep2Title: "اختر ملف PDF",
    uploadStep2Desc: "انقر على منطقة الرفع واختر مستند PDF من جهازك.",
    uploadStep2Tip: "يُقبل ملفات PDF فقط. سيقوم النظام تلقائياً بتضمين رمز QR.",
    uploadStep3Title: "حدد تاريخ الإصدار",
    uploadStep3Desc: "اختر التاريخ الذي صدر فيه المستند.",
    uploadStep4Title: "حدد تاريخ الانتهاء",
    uploadStep4Desc: "اختر متى ينتهي المستند.",
    uploadStep4Tip: "يجب أن يكون تاريخ الانتهاء بعد تاريخ الإصدار.",
    uploadStep5Title: "انقر على 'رفع المستند'",
    uploadStep5Desc: "سيقوم النظام بمعالجة ملف PDF وتضمين رمز QR مع شعار جمارك الشارقة وحفظه.",
    
    // QR Codes
    understandingQR: "فهم رموز QR",
    qrOnDocument: "رمز QR على المستند",
    qrWithLogo: "رمز QR مع شعار جمارك الشارقة",
    qrLocation: "يقع في منتصف أسفل ملف PDF",
    howQRWorks: "كيف تعمل رموز QR",
    qrPoint1: "كل تصريح له رمز QR فريد",
    qrPoint2: "يتم تضمين رمز QR تلقائياً عند رفع المستند",
    qrPoint3: "مسح رمز QR يفتح المستند الرسمي",
    qrPoint4: "يتضمن رمز QR شعار جمارك الشارقة للمصداقية",
    qrPoint5: "تعمل رموز QR على مدار الساعة - بدون توقف",
    scanningInstructions: "تعليمات المسح",
    scanStep1: "افتح تطبيق الكاميرا في هاتفك",
    scanStep2: "وجّه الكاميرا نحو رمز QR على المستند",
    scanStep3: "انقر على الإشعار الذي يظهر",
    scanStep4: "سيفتح المستند تلقائياً",
    
    // Downloading
    downloadingDocuments: "تحميل المستندات",
    downloadStep1Title: "اذهب إلى تفاصيل التصريح",
    downloadStep1Desc: "انقر على أي بطاقة تصريح لعرض تفاصيلها.",
    downloadStep2Title: "انقر على زر التحميل",
    downloadStep2Desc: "انقر على زر 'تحميل المستند' لحفظ ملف PDF مع رمز QR المضمن.",
    downloadStep2Tip: "يتضمن ملف PDF المحمّل رمز QR الذي يرتبط مباشرة بهذا المستند.",
    downloadStep3Title: "تحميل رمز QR فقط",
    downloadStep3Desc: "يمكنك أيضاً تحميل صورة رمز QR فقط بالنقر على 'تحميل رمز QR'.",
    
    // Password
    changingPassword: "تغيير كلمة المرور",
    passwordStep1Title: "اذهب إلى الإعدادات",
    passwordStep1Desc: "انقر على 'الإعدادات' في قائمة التنقل على اليسار.",
    passwordStep2Title: "أدخل كلمة المرور الحالية",
    passwordStep2Desc: "اكتب كلمة المرور الحالية للتحقق من هويتك.",
    passwordStep3Title: "أدخل كلمة المرور الجديدة",
    passwordStep3Desc: "اكتب كلمة المرور الجديدة (6 أحرف على الأقل).",
    passwordStep3Tip: "اختر كلمة مرور قوية تحتوي على حروف وأرقام ورموز.",
    passwordStep4Title: "تأكيد كلمة المرور الجديدة",
    passwordStep4Desc: "اكتب كلمة المرور الجديدة مرة أخرى للتأكيد.",
    passwordStep5Title: "انقر على 'تغيير كلمة المرور'",
    passwordStep5Desc: "سيتم تحديث كلمة المرور فوراً.",
    
    // Admin Features
    adminFeatures: "ميزات المسؤول",
    adminFeaturesDesc: "الميزات التالية متاحة فقط للمسؤولين.",
    
    // Managing Users
    managingUsers: "إدارة المستخدمين",
    usersManagementPage: "صفحة إدارة المستخدمين",
    users: "المستخدمون",
    administrator: "مسؤول النظام",
    regularUser: "مستخدم عادي",
    creatingNewUser: "إنشاء مستخدم جديد",
    userStep1Title: "اذهب إلى صفحة المستخدمين",
    userStep1Desc: "انقر على 'المستخدمون' في قائمة التنقل.",
    userStep2Title: "انقر على 'إنشاء مستخدم'",
    userStep2Desc: "انقر على الزر الأزرق في الزاوية العلوية اليمنى.",
    userStep3Title: "املأ تفاصيل المستخدم",
    userStep3Desc: "أدخل اسم المستخدم وكلمة المرور والاسم ورقم الموظف واختر الدور (مسؤول أو مستخدم).",
    userStep3Tip: "المستخدمون العاديون يمكنهم فقط إنشاء التصاريح. المسؤولون لديهم صلاحيات كاملة.",
    editingUser: "تعديل مستخدم",
    editStep1Title: "ابحث عن المستخدم",
    editStep1Desc: "استخدم مربع البحث للعثور على المستخدم الذي تريد تعديله.",
    editStep2Title: "انقر على زر التعديل",
    editStep2Desc: "انقر على الزر الأزرق 'تعديل' في صف المستخدم.",
    editStep3Title: "حدّث المعلومات",
    editStep3Desc: "غيّر الاسم أو رقم الموظف أو الدور أو كلمة المرور حسب الحاجة.",
    deletingUser: "حذف مستخدم",
    deleteUserStep1Title: "انقر على زر الحذف",
    deleteUserStep1Desc: "انقر على الزر الأحمر 'حذف' في صف المستخدم.",
    deleteUserStep1Tip: "لا يمكنك حذف حسابك أو حساب المسؤول الرئيسي.",
    deleteUserStep2Title: "تأكيد الحذف",
    deleteUserStep2Desc: "انقر على 'موافق' في نافذة التأكيد لحذف المستخدم نهائياً.",
    
    // Deleting Permissions
    deletingPermissions: "حذف التصاريح",
    deleteWarningTitle: "تحذير مهم",
    deleteWarningText: "حذف التصريح سيحذف أيضاً جميع المستندات المرتبطة به. لا يمكن التراجع عن هذا الإجراء.",
    deletePermStep1Title: "ابحث عن التصريح",
    deletePermStep1Desc: "اذهب إلى صفحة التصاريح وحدد التصريح الذي تريد حذفه.",
    deletePermStep2Title: "انقر على زر الحذف",
    deletePermStep2Desc: "انقر على الزر الأحمر 'حذف' على بطاقة التصريح.",
    deletePermStep3Title: "تأكيد الحذف",
    deletePermStep3Desc: "انقر على 'موافق' في نافذة التأكيد.",
    
    // Audit Logs
    viewingAuditLogs: "عرض سجلات المراجعة",
    auditLogsPage: "صفحة سجلات المراجعة",
    auditLogs: "سجلات المراجعة",
    auditLogsIntro: "تتبع سجلات المراجعة جميع الإجراءات المهمة في النظام:",
    loginAction: "محاولات تسجيل الدخول (نجاح/فشل)",
    userCreatedAction: "حسابات المستخدمين الجديدة",
    userUpdatedAction: "تغيير معلومات المستخدم",
    userDeletedAction: "حذف حسابات المستخدمين",
    permissionCreatedAction: "إضافة تصاريح جديدة",
    permissionUpdatedAction: "تعديل معرفات الخزانات",
    permissionDeletedAction: "حذف التصاريح",
    documentUploadedAction: "رفع ملفات PDF",
    passwordChangedAction: "تحديث كلمات المرور",
    auditStep1Title: "اذهب إلى سجلات المراجعة",
    auditStep1Desc: "انقر على 'سجلات المراجعة' في قائمة التنقل.",
    auditStep2Title: "تصفية حسب الحالة",
    auditStep2Desc: "استخدم القائمة المنسدلة للحالة للتصفية حسب النجاح أو الفشل.",
    auditStep3Title: "تصفية حسب الإجراء",
    auditStep3Desc: "استخدم القائمة المنسدلة للإجراء للتصفية حسب أنواع الإجراءات المحددة.",
    
    // Login Background
    customizingBackground: "تخصيص خلفية تسجيل الدخول",
    bgStep1Title: "اذهب إلى الإعدادات",
    bgStep1Desc: "انقر على 'الإعدادات' في قائمة التنقل.",
    bgStep2Title: "انتقل إلى خلفية تسجيل الدخول",
    bgStep2Desc: "ابحث عن قسم 'صورة خلفية تسجيل الدخول'.",
    bgStep3Title: "انقر على اختيار صورة",
    bgStep3Desc: "انقر على الزر واختر صورة بتنسيق JPEG أو PNG أو WebP.",
    bgStep3Tip: "للحصول على أفضل النتائج، استخدم صورة عالية الدقة (1920x1080 أو أكبر).",
    bgStep4Title: "انتظر الرفع",
    bgStep4Desc: "سيتم رفع الصورة وتطبيقها فوراً على صفحة تسجيل الدخول.",
    
    // FAQ
    faq: "الأسئلة الشائعة",
    faq1Q: "هل يمكن أن يكون نفس الخزان في تصاريح متعددة؟",
    faq1A: "نعم، يمكن تخصيص نفس معرف الخزان لتصاريح متعددة.",
    faq2Q: "ماذا يحدث لرمز QR عند رفع مستند جديد؟",
    faq2A: "يتم إنشاء رمز QR جديد وتضمينه في المستند الجديد. يصبح المستند القديم غير نشط.",
    faq3Q: "لماذا يظهر مسح رمز QR صفحة تحميل أولاً؟",
    faq3A: "تضمن صفحة التحميل أن المستند يُحمّل بشكل صحيح حتى لو احتاج الخادم لحظة للاستجابة.",
    faq4Q: "هل يمكن للمستخدمين العاديين حذف التصاريح؟",
    faq4A: "لا، فقط المسؤولون يمكنهم حذف التصاريح. المستخدمون العاديون يمكنهم إنشاء وتعديل التصاريح.",
    faq5Q: "كيف أتواصل مع الدعم؟",
    faq5A: "يرجى التواصل مع مسؤول النظام للحصول على الدعم.",
    
    // Footer
    footer: "نظام TankGuard لإدارة المستندات • دليل المستخدم الإصدار 1.0",
    
    // Common
    tip: "نصيحة:",
    active: "نشط",
    edit: "تعديل",
    delete: "حذف",
    admin: "مسؤول",
    user: "مستخدم",
    login: "تسجيل دخول",
    userLoggedIn: "تم تسجيل دخول المستخدم",
    permissionCreated: "تم إنشاء #534",
    documentUploaded: "تم رفع PDF",
  }
};

const HelpSection = ({ title, icon: Icon, children, defaultOpen = false, isRtl }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="bg-white border border-slate-200 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
      >
        <div className={`flex items-center gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
          <div className="bg-blue-100 p-2 rounded-lg">
            <Icon className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 pt-0 border-t border-slate-100">
          {children}
        </div>
      )}
    </Card>
  );
};

const StepCard = ({ number, title, description, tips, t, isRtl }) => (
  <div className={`flex gap-4 p-4 bg-slate-50 rounded-lg ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
      {number}
    </div>
    <div className="flex-1">
      <h4 className="font-semibold text-slate-900 mb-1">{title}</h4>
      <p className="text-slate-600 text-sm mb-2">{description}</p>
      {tips && (
        <div className="bg-blue-50 border border-blue-200 rounded p-2 mt-2">
          <p className="text-blue-800 text-xs"><strong>💡 {t.tip}</strong> {tips}</p>
        </div>
      )}
    </div>
  </div>
);

const Help = () => {
  const [userRole, setUserRole] = useState(null);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('helpLanguage') || 'en';
  });

  const t = translations[language];
  const isRtl = language === 'ar';

  useEffect(() => {
    fetchUserRole();
  }, []);

  useEffect(() => {
    localStorage.setItem('helpLanguage', language);
  }, [language]);

  const fetchUserRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserRole(response.data.role);
    } catch (error) {
      console.error('Failed to fetch user role');
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <Layout>
      <div data-testid="help-page" className={`space-y-6 ${isRtl ? 'rtl' : 'ltr'}`} dir={isRtl ? 'rtl' : 'ltr'}>
        {/* Header with Language Toggle */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl p-8 text-white relative">
          {/* Language Toggle Button - Top Right */}
          <Button
            onClick={toggleLanguage}
            className={`absolute top-4 ${isRtl ? 'left-4' : 'right-4'} bg-white text-blue-800 hover:bg-blue-50 font-semibold px-4 py-2 flex items-center gap-2 shadow-lg`}
          >
            <Globe className="w-5 h-5" />
            <span className="text-base">{language === 'en' ? 'العربية | Arabic' : 'English | الإنجليزية'}</span>
          </Button>
          
          <div className={`flex items-center gap-4 mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
            <div className="bg-white/20 p-3 rounded-lg">
              <BookOpen className="w-8 h-8" />
            </div>
            <div className={isRtl ? 'text-right' : ''}>
              <h1 className="text-3xl font-bold">{t.pageTitle}</h1>
              <p className="text-blue-100 mt-1">
                {userRole === 'admin' ? t.adminGuide : t.userGuide}
              </p>
            </div>
          </div>
          <p className={`text-blue-100 max-w-2xl ${isRtl ? 'text-right' : ''}`}>
            {t.welcomeText}
          </p>
        </div>

        {/* Quick Start */}
        <Card className="p-6 bg-white border-2 border-blue-200">
          <h2 className={`text-xl font-bold text-slate-900 mb-4 flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
            🚀 {t.quickStart}
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className={`p-4 bg-blue-50 rounded-lg ${isRtl ? 'text-right' : 'text-center'}`}>
              <div className="text-3xl mb-2">1️⃣</div>
              <h4 className="font-semibold text-slate-900">{t.step1Title}</h4>
              <p className="text-sm text-slate-600">{t.step1Desc}</p>
            </div>
            <div className={`p-4 bg-blue-50 rounded-lg ${isRtl ? 'text-right' : 'text-center'}`}>
              <div className="text-3xl mb-2">2️⃣</div>
              <h4 className="font-semibold text-slate-900">{t.step2Title}</h4>
              <p className="text-sm text-slate-600">{t.step2Desc}</p>
            </div>
            <div className={`p-4 bg-blue-50 rounded-lg ${isRtl ? 'text-right' : 'text-center'}`}>
              <div className="text-3xl mb-2">3️⃣</div>
              <h4 className="font-semibold text-slate-900">{t.step3Title}</h4>
              <p className="text-sm text-slate-600">{t.step3Desc}</p>
            </div>
          </div>
        </Card>

        {/* Common Features for All Users */}
        <div className="space-y-4">
          <h2 className={`text-2xl font-bold text-slate-900 ${isRtl ? 'text-right' : ''}`}>📖 {t.featureGuide}</h2>
          
          <HelpSection title={t.loggingIn} icon={LogIn} defaultOpen={true} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                <p className="text-center text-slate-500 text-sm mb-2">📸 {t.loginScreen}</p>
                <div className="bg-white rounded p-4 max-w-sm mx-auto shadow-sm">
                  <div className="text-center mb-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-lg mx-auto mb-2 flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="font-bold">Admin Login</h4>
                  </div>
                  <div className="space-y-2">
                    <div className="h-8 bg-slate-100 rounded border"></div>
                    <div className="h-8 bg-slate-100 rounded border"></div>
                    <div className="h-8 bg-slate-900 rounded"></div>
                  </div>
                </div>
              </div>
              <StepCard number="1" title={t.loginStep1Title} description={t.loginStep1Desc} t={t} isRtl={isRtl} />
              <StepCard number="2" title={t.loginStep2Title} description={t.loginStep2Desc} t={t} isRtl={isRtl} />
              <StepCard number="3" title={t.loginStep3Title} description={t.loginStep3Desc} tips={t.loginTip} t={t} isRtl={isRtl} />
            </div>
          </HelpSection>

          <HelpSection title={t.viewingPermissions} icon={Shield} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <p className={`text-slate-600 ${isRtl ? 'text-right' : ''}`}>{t.permissionIntro}</p>
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                <p className="text-center text-slate-500 text-sm mb-2">📸 {t.permissionCardExample}</p>
                <div className="bg-white rounded p-4 max-w-xs mx-auto shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <div className="bg-slate-900 text-white px-3 py-1 rounded font-mono text-sm">#534</div>
                    <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">{t.active}</span>
                  </div>
                  <p className="text-sm text-slate-600"><strong>5</strong> {t.tanksAssigned}</p>
                  <p className="text-xs text-slate-400 mt-1">{t.created} 01/15/2026</p>
                  <div className="flex gap-2 mt-3 pt-3 border-t">
                    <div className="flex-1 h-7 bg-blue-50 rounded border border-blue-200 flex items-center justify-center text-xs text-blue-600">{t.edit}</div>
                    <div className="flex-1 h-7 bg-red-50 rounded border border-red-200 flex items-center justify-center text-xs text-red-600">{t.delete}</div>
                  </div>
                </div>
              </div>
              <ul className={`list-disc space-y-2 text-slate-600 ${isRtl ? 'list-inside text-right' : 'list-inside'}`}>
                <li><strong>{t.permissionNumber}</strong> {t.permissionNumberDesc}</li>
                <li><strong>{t.statusBadge}</strong> {t.statusBadgeDesc}</li>
                <li><strong>{t.tankCount}</strong> {t.tankCountDesc}</li>
                <li><strong>{t.creationDate}</strong> {t.creationDateDesc}</li>
              </ul>
            </div>
          </HelpSection>

          <HelpSection title={t.creatingPermission} icon={FileText} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                <p className="text-center text-slate-500 text-sm mb-2">📸 {t.createPermissionDialog}</p>
                <div className="bg-white rounded p-4 max-w-md mx-auto shadow-sm">
                  <h4 className="font-bold text-blue-900 border-b pb-2 mb-3">{t.creatingPermission}</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-slate-600">{t.createPermissionNumber}</label>
                      <div className="h-8 bg-slate-100 rounded border mt-1"></div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-600">{t.tankIdsLabel}</label>
                      <div className="h-16 bg-slate-100 rounded border mt-1"></div>
                    </div>
                    <div className="h-8 bg-slate-900 rounded"></div>
                  </div>
                </div>
              </div>
              <StepCard number="1" title={t.createStep1Title} description={t.createStep1Desc} t={t} isRtl={isRtl} />
              <StepCard number="2" title={t.createStep2Title} description={t.createStep2Desc} tips={t.createStep2Tip} t={t} isRtl={isRtl} />
              <StepCard number="3" title={t.createStep3Title} description={t.createStep3Desc} tips={t.createStep3Tip} t={t} isRtl={isRtl} />
              <StepCard number="4" title={t.createStep4Title} description={t.createStep4Desc} t={t} isRtl={isRtl} />
            </div>
          </HelpSection>

          <HelpSection title={t.uploadingDocuments} icon={Upload} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                <p className="text-center text-slate-500 text-sm mb-2">📸 {t.uploadDocumentSection}</p>
                <div className="bg-white rounded p-4 max-w-md mx-auto shadow-sm">
                  <h4 className="font-semibold mb-3">{t.uploadNewDocument}</h4>
                  <div className="space-y-3">
                    <div className="border-2 border-dashed border-slate-300 rounded p-4 text-center">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">{t.clickToUpload}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-slate-600">{t.issueDate}</label>
                        <div className="h-8 bg-slate-100 rounded border mt-1"></div>
                      </div>
                      <div>
                        <label className="text-xs text-slate-600">{t.expiryDate}</label>
                        <div className="h-8 bg-slate-100 rounded border mt-1"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <StepCard number="1" title={t.uploadStep1Title} description={t.uploadStep1Desc} t={t} isRtl={isRtl} />
              <StepCard number="2" title={t.uploadStep2Title} description={t.uploadStep2Desc} tips={t.uploadStep2Tip} t={t} isRtl={isRtl} />
              <StepCard number="3" title={t.uploadStep3Title} description={t.uploadStep3Desc} t={t} isRtl={isRtl} />
              <StepCard number="4" title={t.uploadStep4Title} description={t.uploadStep4Desc} tips={t.uploadStep4Tip} t={t} isRtl={isRtl} />
              <StepCard number="5" title={t.uploadStep5Title} description={t.uploadStep5Desc} t={t} isRtl={isRtl} />
            </div>
          </HelpSection>

          <HelpSection title={t.understandingQR} icon={QrCode} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                <p className="text-center text-slate-500 text-sm mb-2">📸 {t.qrOnDocument}</p>
                <div className="bg-white rounded p-4 max-w-xs mx-auto shadow-sm text-center">
                  <div className="w-24 h-24 bg-slate-200 rounded mx-auto mb-2 flex items-center justify-center">
                    <QrCode className="w-16 h-16 text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500">{t.qrWithLogo}</p>
                  <p className="text-xs text-slate-400 mt-1">{t.qrLocation}</p>
                </div>
              </div>
              <div className={`bg-green-50 border border-green-200 rounded-lg p-4 ${isRtl ? 'text-right' : ''}`}>
                <h4 className="font-semibold text-green-900 mb-2">✅ {t.howQRWorks}</h4>
                <ul className={`list-disc space-y-1 text-green-800 text-sm ${isRtl ? 'mr-4' : 'ml-4'}`}>
                  <li>{t.qrPoint1}</li>
                  <li>{t.qrPoint2}</li>
                  <li>{t.qrPoint3}</li>
                  <li>{t.qrPoint4}</li>
                  <li>{t.qrPoint5}</li>
                </ul>
              </div>
              <div className={`bg-blue-50 border border-blue-200 rounded-lg p-4 ${isRtl ? 'text-right' : ''}`}>
                <h4 className="font-semibold text-blue-900 mb-2">📱 {t.scanningInstructions}</h4>
                <ol className={`list-decimal space-y-1 text-blue-800 text-sm ${isRtl ? 'mr-4' : 'ml-4'}`}>
                  <li>{t.scanStep1}</li>
                  <li>{t.scanStep2}</li>
                  <li>{t.scanStep3}</li>
                  <li>{t.scanStep4}</li>
                </ol>
              </div>
            </div>
          </HelpSection>

          <HelpSection title={t.downloadingDocuments} icon={Download} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <StepCard number="1" title={t.downloadStep1Title} description={t.downloadStep1Desc} t={t} isRtl={isRtl} />
              <StepCard number="2" title={t.downloadStep2Title} description={t.downloadStep2Desc} tips={t.downloadStep2Tip} t={t} isRtl={isRtl} />
              <StepCard number="3" title={t.downloadStep3Title} description={t.downloadStep3Desc} t={t} isRtl={isRtl} />
            </div>
          </HelpSection>

          <HelpSection title={t.changingPassword} icon={Lock} isRtl={isRtl}>
            <div className="space-y-4 mt-4">
              <StepCard number="1" title={t.passwordStep1Title} description={t.passwordStep1Desc} t={t} isRtl={isRtl} />
              <StepCard number="2" title={t.passwordStep2Title} description={t.passwordStep2Desc} t={t} isRtl={isRtl} />
              <StepCard number="3" title={t.passwordStep3Title} description={t.passwordStep3Desc} tips={t.passwordStep3Tip} t={t} isRtl={isRtl} />
              <StepCard number="4" title={t.passwordStep4Title} description={t.passwordStep4Desc} t={t} isRtl={isRtl} />
              <StepCard number="5" title={t.passwordStep5Title} description={t.passwordStep5Desc} t={t} isRtl={isRtl} />
            </div>
          </HelpSection>

          {/* Admin-Only Sections */}
          {userRole === 'admin' && (
            <>
              <div className="border-t-2 border-slate-200 pt-6 mt-8">
                <h2 className={`text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
                  👑 {t.adminFeatures}
                </h2>
                <p className={`text-slate-600 mb-4 ${isRtl ? 'text-right' : ''}`}>{t.adminFeaturesDesc}</p>
              </div>

              <HelpSection title={t.managingUsers} icon={Settings} isRtl={isRtl}>
                <div className="space-y-4 mt-4">
                  <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                    <p className="text-center text-slate-500 text-sm mb-2">📸 {t.usersManagementPage}</p>
                    <div className="bg-white rounded p-4 max-w-md mx-auto shadow-sm">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-bold">{t.users}</h4>
                        <div className="h-7 w-24 bg-slate-900 rounded"></div>
                      </div>
                      <div className="h-8 bg-slate-100 rounded border mb-3"></div>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium text-sm">admin</p>
                            <p className="text-xs text-slate-500">{t.administrator}</p>
                          </div>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{t.admin}</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-slate-50 rounded">
                          <div>
                            <p className="font-medium text-sm">user1</p>
                            <p className="text-xs text-slate-500">{t.regularUser}</p>
                          </div>
                          <span className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">{t.user}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <h4 className={`font-semibold text-slate-900 ${isRtl ? 'text-right' : ''}`}>{t.creatingNewUser}</h4>
                  <StepCard number="1" title={t.userStep1Title} description={t.userStep1Desc} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.userStep2Title} description={t.userStep2Desc} t={t} isRtl={isRtl} />
                  <StepCard number="3" title={t.userStep3Title} description={t.userStep3Desc} tips={t.userStep3Tip} t={t} isRtl={isRtl} />
                  
                  <h4 className={`font-semibold text-slate-900 mt-6 ${isRtl ? 'text-right' : ''}`}>{t.editingUser}</h4>
                  <StepCard number="1" title={t.editStep1Title} description={t.editStep1Desc} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.editStep2Title} description={t.editStep2Desc} t={t} isRtl={isRtl} />
                  <StepCard number="3" title={t.editStep3Title} description={t.editStep3Desc} t={t} isRtl={isRtl} />
                  
                  <h4 className={`font-semibold text-slate-900 mt-6 ${isRtl ? 'text-right' : ''}`}>{t.deletingUser}</h4>
                  <StepCard number="1" title={t.deleteUserStep1Title} description={t.deleteUserStep1Desc} tips={t.deleteUserStep1Tip} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.deleteUserStep2Title} description={t.deleteUserStep2Desc} t={t} isRtl={isRtl} />
                </div>
              </HelpSection>

              <HelpSection title={t.deletingPermissions} icon={Trash2} isRtl={isRtl}>
                <div className="space-y-4 mt-4">
                  <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${isRtl ? 'text-right' : ''}`}>
                    <h4 className="font-semibold text-red-900 mb-2">⚠️ {t.deleteWarningTitle}</h4>
                    <p className="text-red-800 text-sm">{t.deleteWarningText}</p>
                  </div>
                  <StepCard number="1" title={t.deletePermStep1Title} description={t.deletePermStep1Desc} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.deletePermStep2Title} description={t.deletePermStep2Desc} t={t} isRtl={isRtl} />
                  <StepCard number="3" title={t.deletePermStep3Title} description={t.deletePermStep3Desc} t={t} isRtl={isRtl} />
                </div>
              </HelpSection>

              <HelpSection title={t.viewingAuditLogs} icon={FileText} isRtl={isRtl}>
                <div className="space-y-4 mt-4">
                  <div className="bg-slate-100 rounded-lg p-4 border-2 border-dashed border-slate-300">
                    <p className="text-center text-slate-500 text-sm mb-2">📸 {t.auditLogsPage}</p>
                    <div className="bg-white rounded p-4 max-w-md mx-auto shadow-sm">
                      <h4 className="font-bold mb-3">{t.auditLogs}</h4>
                      <div className="flex gap-2 mb-3">
                        <div className="h-8 flex-1 bg-slate-100 rounded border"></div>
                        <div className="h-8 flex-1 bg-slate-100 rounded border"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2 bg-green-50 rounded text-xs">
                          <span className="font-medium">{t.login}</span> - {t.userLoggedIn}
                        </div>
                        <div className="p-2 bg-blue-50 rounded text-xs">
                          <span className="font-medium">PERMISSION_CREATED</span> - {t.permissionCreated}
                        </div>
                        <div className="p-2 bg-yellow-50 rounded text-xs">
                          <span className="font-medium">DOCUMENT_UPLOADED</span> - {t.documentUploaded}
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className={`text-slate-600 ${isRtl ? 'text-right' : ''}`}>{t.auditLogsIntro}</p>
                  <ul className={`list-disc space-y-1 text-slate-600 text-sm ${isRtl ? 'mr-4 text-right' : 'ml-4'}`}>
                    <li><strong>LOGIN:</strong> {t.loginAction}</li>
                    <li><strong>USER_CREATED:</strong> {t.userCreatedAction}</li>
                    <li><strong>USER_UPDATED:</strong> {t.userUpdatedAction}</li>
                    <li><strong>USER_DELETED:</strong> {t.userDeletedAction}</li>
                    <li><strong>PERMISSION_CREATED:</strong> {t.permissionCreatedAction}</li>
                    <li><strong>PERMISSION_UPDATED:</strong> {t.permissionUpdatedAction}</li>
                    <li><strong>PERMISSION_DELETED:</strong> {t.permissionDeletedAction}</li>
                    <li><strong>DOCUMENT_UPLOADED:</strong> {t.documentUploadedAction}</li>
                    <li><strong>PASSWORD_CHANGED:</strong> {t.passwordChangedAction}</li>
                  </ul>
                  <StepCard number="1" title={t.auditStep1Title} description={t.auditStep1Desc} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.auditStep2Title} description={t.auditStep2Desc} t={t} isRtl={isRtl} />
                  <StepCard number="3" title={t.auditStep3Title} description={t.auditStep3Desc} t={t} isRtl={isRtl} />
                </div>
              </HelpSection>

              <HelpSection title={t.customizingBackground} icon={Image} isRtl={isRtl}>
                <div className="space-y-4 mt-4">
                  <StepCard number="1" title={t.bgStep1Title} description={t.bgStep1Desc} t={t} isRtl={isRtl} />
                  <StepCard number="2" title={t.bgStep2Title} description={t.bgStep2Desc} t={t} isRtl={isRtl} />
                  <StepCard number="3" title={t.bgStep3Title} description={t.bgStep3Desc} tips={t.bgStep3Tip} t={t} isRtl={isRtl} />
                  <StepCard number="4" title={t.bgStep4Title} description={t.bgStep4Desc} t={t} isRtl={isRtl} />
                </div>
              </HelpSection>
            </>
          )}
        </div>

        {/* FAQ Section */}
        <Card className="p-6 bg-white">
          <h2 className={`text-xl font-bold text-slate-900 mb-4 ${isRtl ? 'text-right' : ''}`}>❓ {t.faq}</h2>
          <div className="space-y-4">
            <div className={`border-b border-slate-100 pb-4 ${isRtl ? 'text-right' : ''}`}>
              <h4 className="font-semibold text-slate-900">{t.faq1Q}</h4>
              <p className="text-slate-600 text-sm mt-1">{t.faq1A}</p>
            </div>
            <div className={`border-b border-slate-100 pb-4 ${isRtl ? 'text-right' : ''}`}>
              <h4 className="font-semibold text-slate-900">{t.faq2Q}</h4>
              <p className="text-slate-600 text-sm mt-1">{t.faq2A}</p>
            </div>
            <div className={`border-b border-slate-100 pb-4 ${isRtl ? 'text-right' : ''}`}>
              <h4 className="font-semibold text-slate-900">{t.faq3Q}</h4>
              <p className="text-slate-600 text-sm mt-1">{t.faq3A}</p>
            </div>
            <div className={`border-b border-slate-100 pb-4 ${isRtl ? 'text-right' : ''}`}>
              <h4 className="font-semibold text-slate-900">{t.faq4Q}</h4>
              <p className="text-slate-600 text-sm mt-1">{t.faq4A}</p>
            </div>
            <div className={isRtl ? 'text-right' : ''}>
              <h4 className="font-semibold text-slate-900">{t.faq5Q}</h4>
              <p className="text-slate-600 text-sm mt-1">{t.faq5A}</p>
            </div>
          </div>
        </Card>

        {/* Footer */}
        <div className="text-center text-slate-500 text-sm py-4">
          {t.footer}
        </div>
      </div>
    </Layout>
  );
};

export default Help;
